import { Update, defModule, type TaskMessage, type UpdateResult } from '@st/redux'
import { Effect, pipe } from 'effect'
import type { AuthError } from '@st/sdk'
import { isValidEmail } from '@st/util/email'
import { isEmpty } from '@st/util/json-value'
import { getQueryParams, removeQueryParams } from '@st/util/url'
import { isMatching, match, P } from '@st/pattern'
import { observeAuth, observeLocation } from '~/features/module-runtime'
import {
  completeMfaVerification,
  identifyAnonymousUser,
  identifyAuthenticatedUser,
  loginWithMagicLink,
  loginWithPassword,
  logout as logoutCommand,
  resetPassword,
  sendPasswordResetEmail,
  type AuthOperationOutcome,
  type ActiveMfaSession,
  type PasswordResetEmailError
} from './auth-commands'
import type { AuthStatusEvent, User } from './auth-client-adapter'
import { Routing, type RoutingService } from '~/effects'

export type { PasswordResetEmailError } from './auth-commands'

type InitialAuth = 'pending' | 'loggedIn' | 'loggedOut'

type RecoveryState =
  | { status: 'idle' }
  | { status: 'sendingEmail'; email: string }
  | { status: 'emailSent'; email: string }
  | { status: 'emailFailed'; email: string; error: PasswordResetEmailError }
  | { status: 'resettingPassword' }
  | { status: 'passwordResetSucceeded' }
  | { status: 'passwordResetFailed'; error: AuthError }

type MfaVerification =
  | { status: 'idle'; session: ActiveMfaSession }
  | { status: 'verifying'; session: ActiveMfaSession }
  | { status: 'failed'; session: ActiveMfaSession; error: AuthError }

type SessionState =
  | { status: 'unknown' }
  | { status: 'loggedOut' }
  | { status: 'loggingIn'; email?: string }
  | { status: 'waitingForMFACode'; email?: string; mfa: MfaVerification }
  | { status: 'error'; error: AuthError }
  | { status: 'loggedIn'; user: User }

export type AuthModuleState = SessionState & {
  /** First terminal Auth observation for this app runtime. */
  initialAuth: InitialAuth
  recovery: RecoveryState
}

type AuthChanged = {
  type: 'authChanged'
  event: AuthStatusEvent
}

const observeAuthModuleStatus = observeAuth(
  (event): AuthChanged => ({ type: 'authChanged', event })
)

const observeMagicLink = observeLocation(({ pathname }): MagicLinkDetected | null => {
  const query = getQueryParams(pathname)
  const token = query['t'] ?? query['magic-link-token'] ?? query['token']
  if (!token) {
    return null
  }

  return {
    type: 'magicLinkDetected',
    token,
    cleanedPath: removeQueryParams(pathname, ['t', 'magic-link-token', 'token'])
  }
})

type MagicLinkDetected = {
  type: 'magicLinkDetected'
  token: string
  cleanedPath: string
}

export type AuthMessage =
  | TaskMessage<typeof observeAuthModuleStatus>
  | TaskMessage<typeof observeMagicLink>
  | AuthOperationOutcome
  | { type: 'loginWithEmailPassword'; email: string; password: string }
  | { type: 'completeLoginWithMFACode'; code: string }
  | {
      type: 'resetPassword'
      token: string
      password: string
      passwordConfirmation: string
      name?: string
      cleanedPath: string
    }
  | { type: 'sendPasswordResetEmail'; email: string }
  | { type: 'logout'; destination?: LogoutDestination }

export type LogoutDestination =
  | { type: 'path'; pathname: string; replace?: boolean; beforeLogout?: true }
  | { type: 'url'; url: string }

// A browser can enqueue a click before React removes the control. Those user
// intents are irrelevant outside the phase that presents them; internal
// operation outcomes still enter the exhaustive state chart and fail loudly.
const isIgnoredAuthIntent = isMatching(
  P.union(
    {
      state: { status: P.not(P.union('loggedOut', 'error')) },
      message: { type: 'loginWithEmailPassword' }
    },
    {
      state: { status: P.not('waitingForMFACode') },
      message: { type: 'completeLoginWithMFACode' }
    },
    {
      state: { status: 'waitingForMFACode', mfa: { status: 'verifying' } },
      message: { type: 'completeLoginWithMFACode' }
    },
    {
      state: { status: P.not(P.union('loggedOut', 'error')) },
      message: { type: P.union('sendPasswordResetEmail', 'resetPassword') }
    },
    {
      state: { status: P.not('loggedIn') },
      message: { type: 'logout' }
    }
  )
)

/**
 * Auth topology:
 *
 * unknown --Auth--> loggedOut <--failure-- loggingIn --Auth--> loggedIn
 *                     |                         |
 *                     +--credentials---------->+--MFA--> waitingForMFACode
 *                     |                                      | verify
 *                     +--magic link---------------------------+------> loggedIn
 *
 * Password recovery is an orthogonal auth workflow. Account-profile and MFA
 * settings are deliberately owned by stAccountSettings; sharing the Auth
 * service does not make them session state.
 *
 * The Auth service is the canonical physical session owner. This module
 * projects its replayable status stream into serializable application state
 * and owns only user-facing workflow phases. Accepted user operations are
 * Commands; the service serializes their physical requests and publishes the
 * authoritative resulting identity.
 */
export const authModule = defModule<{ state: AuthModuleState; message: AuthMessage }>({
  init: () => ({
    status: 'unknown',
    initialAuth: 'pending',
    recovery: { status: 'idle' }
  }),
  tasks: authTasks,
  update: pipe(updateAuth, Update.ignoreWhen(isIgnoredAuthIntent))
})

function authTasks(state: AuthModuleState) {
  const tasks = [observeAuthModuleStatus()]
  if (state.status == 'loggedOut' || state.status == 'error') {
    return [...tasks, observeMagicLink()]
  }
  return tasks
}

function updateAuth(
  state: AuthModuleState,
  message: AuthMessage
): UpdateResult<AuthModuleState, AuthMessage> {
  return match({ state, message })
    .returnType<UpdateResult<AuthModuleState, AuthMessage>>()
    .with({ message: { type: 'authChanged' } }, ({ state, message }) =>
      authChanged(state, message.event)
    )
    .with(
      { state: { status: 'loggedIn' }, message: { type: 'magicLinkDetected' } },
      ({ state }) => state
    )
    .with({ message: { type: 'magicLinkDetected' } }, ({ state, message }) => [
      withSession(state, { status: 'loggingIn' }),
      loginWithMagicLink(message.token, message.cleanedPath)
    ])
    .with(
      {
        state: { status: P.union('loggedOut', 'error') },
        message: { type: 'loginWithEmailPassword' }
      },
      ({ state, message }) => startPasswordLogin(state, message.email, message.password)
    )
    .with(
      { state: { status: 'loggingIn' }, message: { type: 'mfaRequired' } },
      ({ state, message }) =>
        withSession(state, {
          status: 'waitingForMFACode',
          email: state.email,
          mfa: { status: 'idle', session: message.session }
        })
    )
    .with(
      { state: { status: 'loggingIn' }, message: { type: 'loginFailed' } },
      ({ state, message }) => withSession(state, { status: 'error', error: message.error })
    )
    .with(
      {
        state: {
          status: 'waitingForMFACode',
          mfa: { status: P.union('idle', 'failed') }
        },
        message: { type: 'completeLoginWithMFACode' }
      },
      ({ state, message }) => [
        withSession(state, {
          status: 'waitingForMFACode',
          email: state.email,
          mfa: { status: 'verifying', session: state.mfa.session }
        }),
        completeMfaVerification(state.mfa.session.sessionId, message.code)
      ]
    )
    .with(
      {
        state: { status: 'waitingForMFACode', mfa: { status: 'verifying' } },
        message: { type: 'mfaVerificationFailed' }
      },
      ({ state, message }) =>
        withSession(state, {
          status: 'waitingForMFACode',
          email: state.email,
          mfa: { status: 'failed', session: state.mfa.session, error: message.error }
        })
    )
    .with(
      {
        state: { status: P.union('loggedOut', 'error') },
        message: { type: 'sendPasswordResetEmail' }
      },
      ({ state, message }) => [
        { ...state, recovery: { status: 'sendingEmail', email: message.email } },
        sendPasswordResetEmail(message.email)
      ]
    )
    .with(
      {
        state: { recovery: { status: 'sendingEmail' } },
        message: { type: 'passwordResetEmailSent' }
      },
      ({ state, message }) => ({
        ...state,
        recovery: { status: 'emailSent', email: message.email }
      })
    )
    .with(
      {
        state: { recovery: { status: 'sendingEmail' } },
        message: { type: 'passwordResetEmailFailed' }
      },
      ({ state, message }) => ({
        ...state,
        recovery: {
          status: 'emailFailed',
          email: state.recovery.email,
          error: message.error
        }
      })
    )
    .with(
      {
        state: { status: P.union('loggedOut', 'error') },
        message: { type: 'resetPassword' }
      },
      ({ state, message }) => [
        { ...state, recovery: { status: 'resettingPassword' } },
        resetPassword(message)
      ]
    )
    .with(
      {
        state: { recovery: { status: 'resettingPassword' } },
        message: { type: 'passwordResetSucceeded' }
      },
      ({ state }) => ({ ...state, recovery: { status: 'passwordResetSucceeded' } })
    )
    .with(
      {
        state: { recovery: { status: 'resettingPassword' } },
        message: { type: 'passwordResetFailed' }
      },
      ({ state, message }) => ({
        ...state,
        recovery: { status: 'passwordResetFailed', error: message.error }
      })
    )
    .with(
      { state: { status: 'loggedIn' }, message: { type: 'logout' } },
      ({ state, message }) => [state, runLogout(message.destination)]
    )
    .otherwise(({ state, message }) => unexpectedMessage(state, message))
}

function startPasswordLogin(
  state: AuthModuleState,
  email: string,
  password: string
): UpdateResult<AuthModuleState, AuthMessage> {
  const error = validateCredentials(email, password)
  if (error) {
    return withSession(state, { status: 'error', error })
  }

  return [withSession(state, { status: 'loggingIn', email }), loginWithPassword(email, password)]
}

function validateCredentials(email: string, password: string): AuthError | undefined {
  if (isEmpty(email)) {
    return { type: 'missing_email', message: 'Email is required' }
  }
  if (!isValidEmail(email)) {
    return { type: 'invalid_email', message: 'Invalid email address' }
  }
  if (isEmpty(password)) {
    return { type: 'missing_password', message: 'Password is required' }
  }
  return undefined
}

function authChanged(
  state: AuthModuleState,
  event: AuthStatusEvent
): UpdateResult<AuthModuleState, AuthMessage> {
  return match(event)
    .returnType<UpdateResult<AuthModuleState, AuthMessage>>()
    .with({ type: 'loggedIn' }, ({ user }) => [
      {
        status: 'loggedIn',
        user,
        initialAuth: resolveInitialAuth(state.initialAuth, 'loggedIn'),
        recovery: recoveryAfterLogin(state.recovery)
      },
      identifyAuthenticatedUser(user)
    ])
    .with({ type: 'loggedOut' }, () => [
      {
        status: 'loggedOut',
        initialAuth: resolveInitialAuth(state.initialAuth, 'loggedOut'),
        recovery: { status: 'idle' }
      },
      identifyAnonymousUser()
    ])
    .with({ type: 'loginFailed' }, ({ error }) => withSession(state, { status: 'error', error }))
    .exhaustive()
}

function recoveryAfterLogin(recovery: RecoveryState): RecoveryState {
  return match(recovery)
    .with({ status: 'resettingPassword' }, (recovery) => recovery)
    .otherwise(() => ({ status: 'idle' }))
}

function resolveInitialAuth(
  current: InitialAuth,
  observed: Exclude<InitialAuth, 'pending'>
): InitialAuth {
  if (current == 'pending') {
    return observed
  }
  return current
}

function withSession(state: AuthModuleState, session: SessionState): AuthModuleState {
  return { ...session, initialAuth: state.initialAuth, recovery: state.recovery }
}

function unexpectedMessage(state: AuthModuleState, message: AuthMessage): never {
  throw new Error(`Unexpected auth message ${message.type} while ${state.status}`)
}

export function loginWithEmailPassword(opts: { email: string; password: string }): AuthMessage {
  return { type: 'loginWithEmailPassword', email: opts.email, password: opts.password }
}

function runLogout(destination: LogoutDestination | undefined) {
  return Effect.gen(function* () {
    const routing = yield* Routing

    if (destination?.type == 'path' && destination.beforeLogout) {
      yield* navigateToLogoutPath(routing, destination)
    }

    yield* logoutCommand()

    if (destination?.type == 'path' && !destination.beforeLogout) {
      yield* navigateToLogoutPath(routing, destination)
    }

    if (destination?.type == 'url') {
      yield* routing.redirectTo(destination.url)
    }

    return null
  })
}

function navigateToLogoutPath(
  routing: RoutingService,
  destination: Extract<LogoutDestination, { type: 'path' }>
) {
  if (destination.replace) {
    return routing.replacePath(destination.pathname)
  }
  return routing.pushPath(destination.pathname)
}

export function logout(destination?: LogoutDestination): AuthMessage {
  return { type: 'logout', destination }
}

export function currentUser(state: AuthModuleState): User | undefined {
  return match(state)
    .with({ status: 'loggedIn' }, ({ user }) => user)
    .otherwise(() => undefined)
}
